import { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { CATEGORIES } from "@/lib/site";

export function UploadZone({ onUploaded, folders = [] }) {
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [cover, setCover] = useState(null);
  const [title, setTitle] = useState("");
  const [brand, setBrand] = useState("");
  const [category, setCategory] = useState("Demo");
  const [categoriesSel, setCategoriesSel] = useState(["Demo"]);
  const [description, setDescription] = useState("");
  const [folderId, setFolderId] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);
  const coverInput = useRef(null);

  const pickFile = (f) => {
    if (!f) return;
    if (!/\.(mp4|mov)$/i.test(f.name)) {
      toast.error("Only MP4 or MOV files are allowed.");
      return;
    }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ""));
  };

  const submit = async () => {
    if (!file) {
      toast.error("Choose a video file first.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      fd.append("brand", brand);
      fd.append("category", categoriesSel[0] || "Demo");
      fd.append("categories", categoriesSel.join(","));
      fd.append("description", description);
      if (folderId) fd.append("folder_id", folderId);
      if (cover) fd.append("cover", cover);
      const { data } = await api.post("/admin/videos/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded — detected ${data.format === "vertical" ? "9:16 vertical" : "16:9 horizontal"}.`);
      setFile(null); setCover(null); setTitle(""); setBrand(""); setCategory("Demo"); setCategoriesSel(["Demo"]); setFolderId(""); setDescription("");
      onUploaded(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border border-cream-dark bg-white p-6 md:p-8" data-testid="upload-zone">
      <p className="eyebrow text-ink-soft mb-5">Upload a new video</p>
      <div
        data-testid="upload-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); pickFile(e.dataTransfer.files?.[0]); }}
        onClick={() => fileInput.current?.click()}
        className={`cursor-pointer border border-dashed ${dragging ? "border-ink bg-ink/5" : "border-cream-deeper"} p-8 flex flex-col items-center justify-center text-center transition-colors duration-300`}
      >
        <UploadCloud className="w-8 h-8 text-ink-soft mb-3" />
        <p className="text-sm font-semibold text-ink" data-testid="upload-dropzone-label">
          {file ? file.name : "Drag & drop an MP4/MOV, or click to browse"}
        </p>
        <p className="text-xs text-ink-soft mt-1">Orientation (9:16 / 16:9) is detected automatically</p>
        <input ref={fileInput} type="file" accept=".mp4,.mov,video/mp4,video/quicktime" className="hidden"
          data-testid="upload-file-input" onChange={(e) => pickFile(e.target.files?.[0])} />
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label className="eyebrow text-ink-soft">Title</Label>
          <Input data-testid="upload-title-input" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-2 rounded-none h-11" placeholder="e.g. Serum unboxing hook" />
        </div>
        <div>
          <Label className="eyebrow text-ink-soft">Brand</Label>
          <Input data-testid="upload-brand-input" value={brand} onChange={(e) => setBrand(e.target.value)} className="mt-2 rounded-none h-11" placeholder="Brand name" />
        </div>
        <div>
          <Label className="eyebrow text-ink-soft">Categories <span className="normal-case tracking-normal font-normal text-ink-soft/60">(pick one or more)</span></Label>
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="upload-category-picker">
            {CATEGORIES.map((c) => (
              <button
                type="button"
                key={c}
                data-testid={`upload-category-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setCategoriesSel((s) => s.includes(c) ? s.filter((x) => x !== c) : [...s, c])}
                className={`text-[10px] uppercase tracking-[0.12em] font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                  categoriesSel.includes(c) ? "bg-ink text-cream border-ink" : "border-cream-deeper text-ink-soft hover:border-ink"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
        {folders.length > 0 && (
          <div>
            <Label className="eyebrow text-ink-soft">Folder</Label>
            <Select value={folderId || "none"} onValueChange={(v) => setFolderId(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="upload-folder-select" className="mt-2 rounded-none h-11"><SelectValue placeholder="Unfiled" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Unfiled</SelectItem>
                {folders.map((f) => <SelectItem key={f.id} value={f.id} data-testid={`upload-folder-${f.id}`}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="mt-4">
        <Label className="eyebrow text-ink-soft">Description <span className="normal-case tracking-normal font-normal text-ink-soft/60">(shown under the card)</span></Label>
        <Input data-testid="upload-description-input" value={description} onChange={(e) => setDescription(e.target.value)} className="mt-2 rounded-none h-11" placeholder="e.g. A moody unboxing that lets the packaging do the talking" />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-4">
        <button type="button" data-testid="upload-cover-button" onClick={() => coverInput.current?.click()}
          className="text-xs uppercase tracking-[0.14em] font-semibold border border-cream-deeper px-4 py-2.5 text-ink-soft hover:border-ink hover:text-ink transition-colors">
          {cover ? `Cover: ${cover.name}` : "Custom cover image (optional)"}
        </button>
        <input ref={coverInput} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden"
          data-testid="upload-cover-input" onChange={(e) => setCover(e.target.files?.[0] || null)} />
        <button type="button" onClick={submit} disabled={uploading || !file} data-testid="upload-submit-button"
          className="ml-auto bg-ink hover:bg-neutral-800 disabled:opacity-50 text-cream px-8 py-3 text-sm font-semibold transition-colors duration-300">
          {uploading ? "Uploading…" : "Add to Video Wall"}
        </button>
      </div>
    </div>
  );
}
